/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Fragment, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { ProjectService, LocationService, ZoneService } from "@services";
import utils from "@utils";
import {
	ConfirmationModal,
	SearchField,
	TabMenu,
	ScrollDetect,
	LocationZonesModal,
} from "../../../components";

import "./ProjectList.scss";

function ProjectList({ router, user }) {
	const {
		location: { query },
	} = router;
	const { lang } = global;
	const displayCount = 25;
	const [projects, setProjects] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);
	const [managingZones, setManagingZones] = useState(false);
	const [zones, setZones] = useState([]);
	const [locations, setLocations] = useState([]);

	const [filters, setFilters] = useState({
		name: query.name || "",
		status: query.status || "x",
		includeAll: query.includeAll === "true",
		clientId: query.clientId || "",
	});

	const [activeTab, setActiveTab] = useState({
		name: "All",
		tabNumber: 0,
	});

	const [totals, setTotals] = useState({
		active: 0,
		upcoming: 0,
		completed: 0,
		total: 0,
	});

	const _confirmationModal = useRef();

	const calculateTotals = useCallback(() => {
		let active = 0;
		let upcoming = 0;
		let neverEnding = false;
		let completed = 0;
		let total = 0;

		projects.forEach(project => {
			active += project.ActiveJobCount;
			upcoming += project.OpenJobCount;
			completed += project.ClosedJobCount;
			total += project.TotalJobCount;

			// If there is a never ending project, then print the + symbol at the end
			if (project.AnyNeverEndingJob) {
				neverEnding = true;
			}
		});

		upcoming += neverEnding ? "+" : "";

		setTotals({
			active,
			upcoming,
			completed,
			total,
		});
	});

	const tabs = [
		{ text: lang.t("All"), type: "x", tabNumber: 0 },
		{ text: lang.t("Active"), type: "a", tabNumber: 1 },
		{ text: lang.t("Upcoming"), type: "o", tabNumber: 2 },
		{ text: lang.t("Completed"), type: "c", tabNumber: 3 },
	];

	const getLocations = useCallback(() => {
		LocationService.getAll({})
			.then(({ data }) => {
				setLocations(data);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Locations"),
					title: "Error",
				});
			});
	});

	/** Retrieves a paginated list of projects */
	const getProjects = useCallback(() => {
		/* Since this supposed to be a callback made whenever the ScrolLDetect element becomes visible, wrap it in useCallback */
		/* This also helps ensure it uses the correct state values */
		// Remove empty query parameters for the API request
		const queryParams = Object.fromEntries(
			Object.entries(filters).filter(item => item[1] !== ""),
		);

		ProjectService.getAll({ ...queryParams, pageNumber, displayCount })
			.then(({ data }) => {
				const newData = data?.ItemList ?? [];

				setProjects(pageNumber > 1 ? [...projects, ...newData] : newData);
				setPageNumber(pageNumber + 1);
				setHasMore(newData.length >= displayCount);
			})
			.catch(({ response: { data } }) => {
				setProjects([]);
				setPageNumber(1);
				setHasMore(false);
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error loading project list"),
					title: "Error",
				});
			});
	});

	// Execute once component has been mounted for the first time
	useEffect(() => {
		// Check status query parameter to update active tab
		if (query.status && ["x", "a", "o", "c"].includes(query.status)) {
			const statusParam = tabs.find(item => item.type === query.status);

			setActiveTab({
				name: statusParam.text,
				tabNumber: statusParam.tabNumber,
			});
		}
		/*
		it shows all the locations
		*/
		getLocations();
		/*
		load all zones
		*/
		if (user.Organization.UseZones) {
			ZoneService.getAll()
				.then(({ data }) => {
					setZones(data);
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error loading Location Zones"),
						title: "Error",
					});
				});
		}
	}, []);

	// Reset visibility of the scrolldetect element after it has become visible once
	useEffect(() => {
		if (visible) {
			getProjects();
			setVisible(false);
		}
	}, [visible]);

	// Anytime projects is updated, recalculate totals
	useEffect(calculateTotals, [projects]);

	/* Whenever a filter property is changed, retrieve new data from the API by clearing old data and making the ScrollDetect visible */
	/* This automatically calls getProjects */
	useEffect(() => {
		const params = Object.fromEntries(Object.entries(filters).filter(item => item[1] !== ""));
		router.push({ pathname: "/projects", query: params });
		setPageNumber(1);
		setProjects([]);
		setHasMore(true);
	}, [filters.name, filters.status, filters.clientId, filters.includeAll]);

	const onFilter = property => event => {
		// Only update the state if the incoming value is different than the current value
		if (event?.value !== filters[property]) {
			setFilters(prevFilters => ({ ...prevFilters, [property]: event?.value ?? "" }));
		}
	};

	const onClickTab = ({ text: name, type, tabNumber }) => {
		setActiveTab({
			name,
			tabNumber,
		});
		onFilter("status")({ value: type });
	};

	/**
	 * Deletes and removes the selectd project from the list
	 * @param {Number} id The unique id of the selected project
	 */
	const deleteProject = id => {
		ProjectService.delete(id)
			.then(({ data }) => {
				const message = data?.Message ?? lang.t("Project sucessfully deleted");
				utils.notify({
					type: "success",
					timeOut: 1500,
					message,
				});

				setProjects(projects.filter(project => project.Id !== id));
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error deleting project"),
					timeOut: 1500,
				});
			});
	};

	const zonesChangedHandler = () => {
		if (user.Organization.UseZones) {
			ZoneService.getAll()
				.then(({ data }) => {
					setZones(data);
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error loading Location Zones"),
						title: "Error",
					});
				});
		}
	};

	const handleRowClick =
		Id =>
		({
			target: {
				dataset: { preventNavigation },
			},
		}) => {
			if (!preventNavigation) {
				router.push(`/projects/${Id}`);
			}
		};

	const onNameSearch = value => {
		onFilter("name")({ value });
	};

	const onIncludeInactiveChange = event => {
		onFilter("includeAll")({ value: event.target.checked });
	};

	const onNewProject = () => {
		const { push } = router;
		push("/projects/add");
	};

	const onViewJobs = projectId => event => {
		if (event) {
			event.stopPropagation();
		}

		const { push } = router;
		push(`/jobs?projectId=${projectId}`);
	};

	const onViewLocations = projectId => event => {
		if (event) {
			event.stopPropagation();
		}

		const { push } = router;
		push(`/projects/${projectId}?tab=locations`);
	};

	/**
	 * Displays a modal which prompts the user to confirm the deletion of a project
	 * @param {Number} projectId The unique id of the selected project
	 * @param {String} projectName The name of the selected project
	 */
	const onDelete = (projectId, projectName) => event => {
		if (event) {
			event.stopPropagation();
		}

		_confirmationModal.current.openModal({
			title: lang.t("Delete Project"),
			message: `${lang.t("Are you sure you want to delete project")} ${projectName}?`,
			onConfirm: () => deleteProject(projectId),
		});
	};

	return (
		<Fragment>
			<TabMenu
				tabs={tabs}
				activeTab={activeTab.tabNumber}
				onClickTab={onClickTab}
				user={user}
			/>
			<div
				id="project-list-container"
				className="container"
			>
				<div id="project-list-title">
					{lang.t("Projects")}
					<If condition={filters.clientId !== ""}>{` / Client ${filters.clientId}`}</If>
					<If condition={activeTab.name}>{` / ${activeTab.name}`}</If>
				</div>

				<div
					id="project-list-submenu"
					className="row"
				>
					<div className="col-md-3">
						<SearchField
							placeholder={lang.t("Search by Name")}
							onSearch={onNameSearch}
							clearable
							defaultValue={filters.name}
						/>
					</div>
					<div className="col-md-3">
						<div className="checkbox">
							<label htmlFor="includeInactive">
								<input
									type="checkbox"
									name="includeInactive"
									id="includeInactive"
									checked={filters.includeAll}
									onChange={onIncludeInactiveChange}
								/>
								{lang.t("Include Inactive")}
							</label>
						</div>
					</div>
					<div
						id="project-list-btn-add"
						className="col-md-6 text-right"
					>
						<button
							type="button"
							name="new-project"
							className="btn btn-primary pull-right"
							onClick={onNewProject}
						>
							{`+ ${lang.t("New Project")} `}
						</button>
						<If condition={user.IsStaffAdmin && user.Organization.UseZones}>
							<button
								id="manage-zones"
								type="button"
								className="btn btn-primary pull-right btn-manage-zones"
								onClick={() => setManagingZones(true)}
							>
								{lang.t("Manage Zones")}
							</button>
						</If>
					</div>
				</div>

				<div
					id="project-list-table-header"
					className="row hidden-sm hidden-xs"
				>
					<div className="col-md-1">{lang.t("ID")}</div>
					<div className="col-md-2">{lang.t("Name")}</div>
					<div className="col-md-1">{lang.t("Created")}</div>
					<div className="col-md-2">{lang.t("Client")}</div>
					<div
						className="col-md-1"
						title={totals.active}
					>
						{lang.t("Active")}
					</div>
					<div
						className="col-md-1"
						title={totals.upcoming}
					>
						{lang.t("Upcoming")}
					</div>
					<div
						className="col-md-1"
						title={totals.completed}
					>
						{lang.t("Completed")}
					</div>
					<div
						className="col-md-1"
						title={totals.total}
					>
						{lang.t("Total")}
					</div>
					<div className="col-md-2">{lang.t("Actions")}</div>
				</div>
				<div className="row">
					<div
						id="project-list-table-body"
						className="container"
					>
						<For
							each="item"
							index="index"
							of={projects}
						>
							<div
								role="row"
								tabIndex={index}
								className={`row v-align-md ${!item.IsActive ? "disabled-row" : ""}`}
								onClick={handleRowClick(item.Id)}
								key={`project_item_${item.Id}`}
							>
								<div className="col-md-2 col-xs-9 visible-sm-block visible-xs-block truncate">
									<strong>{item.Name}</strong>
								</div>

								<div className="col-md-1 col-xs-3 project-id">
									<span className="visible-sm-inline-block visible-xs-inline-block">
										{`${lang.t("ID")}:`}
										&nbsp;
									</span>
									{item.Id}
								</div>

								<div
									className="col-md-2 hidden-sm hidden-xs truncate"
									title={item.Name}
								>
									{item.Name}
								</div>

								<div className="col-md-1 col-xs-12 project-created-date">
									<span className="visible-sm-inline-block visible-xs-inline-block">
										{`${lang.t("Created Date")}:`}
										&nbsp;
									</span>
									{utils.formatDate(
										`${item.CreateDate}Z`,
										"en-US",
										user.TimeZoneId,
									)}
								</div>

								<div className="col-md-2 col-xs-12 project-client-name">
									<span className="visible-sm-inline-block visible-xs-inline-block">
										{`${lang.t("Client")}:`}
										&nbsp;
									</span>
									{item.ClientName}
								</div>

								<div className="col-md-4 col-xs-6">
									<div className="row project-total">
										<div className="col-md-3 col-xs-12">
											<span className="visible-sm-inline-block visible-xs-inline-block">
												{`${lang.t("Active Jobs")}:`}
												&nbsp;
											</span>
											{item.ActiveJobCount}
										</div>
										<div className="col-md-3 col-xs-12">
											<span className="visible-sm-inline-block visible-xs-inline-block">
												{`${lang.t("Upcoming Jobs")}:`}
												&nbsp;
											</span>
											{item.OpenJobCount}
										</div>
										<div className="col-md-3 col-xs-12">
											<span className="visible-sm-inline-block visible-xs-inline-block">
												{`${lang.t("Completed Jobs")}:`}
												&nbsp;
											</span>
											{item.ClosedJobCount}
										</div>
										<div className="col-md-3 col-xs-12">
											<span className="visible-sm-inline-block visible-xs-inline-block">
												{`${lang.t("Total Jobs")}:`}
												&nbsp;
											</span>
											{item.TotalJobCount}
										</div>
									</div>
								</div>

								<div className="col-md-2 col-xs-6 project-btn-container">
									<button
										type="button"
										id="projects-list-actions-jobs"
										name="projects-list-actions-jobs"
										aria-label={lang.t("View Jobs")}
										onClick={onViewJobs(item.Id)}
										data-prevent-navigation
										disabled={!item.IsActive}
										className="btn btn-icon-only btn-primary"
										title={lang.t("View Jobs")}
									>
										<i className="rkf rkf-business" />
									</button>
									<button
										type="button"
										id="projects-list-actions-locations"
										name="projects-list-actions-locations"
										aria-label={lang.t("View Locations")}
										onClick={onViewLocations(item.Id)}
										data-prevent-navigation
										disabled={!item.IsActive}
										className="btn btn-icon-only btn-primary"
										title={lang.t("View Locations")}
									>
										<i className="rkf rkf-map-pin" />
									</button>
									<button
										type="button"
										id="projects-list-actions-delete"
										name="projects-list-actions-delete"
										aria-label={lang.t("Delete Project")}
										onClick={onDelete(item.Id, item.Name)}
										data-prevent-navigation
										className="btn btn-icon-only btn-primary"
										title={lang.t("Delete Project")}
									>
										<i className="rkf rkf-trash" />
									</button>
								</div>
							</div>
						</For>
						<If condition={hasMore}>
							<ScrollDetect
								className="text-center"
								onVisible={() => setVisible(true)}
							>
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</ScrollDetect>
						</If>
					</div>
				</div>
				<ConfirmationModal ref={_confirmationModal} />
				<LocationZonesModal
					show={managingZones}
					onHide={() => setManagingZones(false)}
					zones={zones}
					locations={locations}
					onZonesChanged={zonesChangedHandler}
				/>
			</div>
		</Fragment>
	);
}

ProjectList.propTypes = {
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
};

ProjectList.defaultProps = {
	user: {},
};

export default ProjectList;
